import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area'
import * as React from 'react'

import { cn } from '@/lib/utils'

/**
 * A scroll container whose scrollbars are real elements.
 *
 * Native bars were the wrong tool here twice over: macOS hides them until you
 * drag, and inside a tall card the horizontal one lands halfway down the page
 * behind whatever is painted after it. These are ordinary DOM nodes — visible,
 * clickable, and styled with the same tokens as everything else.
 *
 * `type="always"` on purpose: a scrollbar that appears only on hover is a
 * scrollbar you have to already know about.
 */
interface ScrollAreaProps
  extends React.ComponentProps<typeof ScrollAreaPrimitive.Root> {
  /** Height the viewport scrolls within once it outgrows it. */
  maxHeight?: number
  /** The scrolling element itself, for scrollIntoView and friends. */
  viewportRef?: React.Ref<HTMLDivElement>
  viewportClassName?: string
}

function ScrollArea({
  className,
  maxHeight,
  viewportRef,
  viewportClassName,
  children,
  ...props
}: ScrollAreaProps) {
  return (
    <ScrollAreaPrimitive.Root type="always" className={cn('relative', className)} {...props}>
      <ScrollAreaPrimitive.Viewport
        ref={viewportRef}
        style={maxHeight ? { maxHeight } : undefined}
        /* Radix wraps children in a display:table div so it can measure them.
           Forcing it back to a block keeps our own w-max sizing predictable. */
        className={cn('size-full [&>div]:!block', viewportClassName)}
      >
        {children}
      </ScrollAreaPrimitive.Viewport>
      <ScrollBar orientation="vertical" />
      <ScrollBar orientation="horizontal" />
      <ScrollAreaPrimitive.Corner className="bg-bg-faint" />
    </ScrollAreaPrimitive.Root>
  )
}

function ScrollBar({
  className,
  orientation = 'vertical',
  ...props
}: React.ComponentProps<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>) {
  return (
    <ScrollAreaPrimitive.ScrollAreaScrollbar
      orientation={orientation}
      className={cn(
        'bg-bg-faint z-20 flex touch-none p-px transition-colors duration-100 ease-out select-none',
        orientation === 'vertical' && 'border-border-subtle h-full w-2.5 border-l',
        orientation === 'horizontal' && 'border-border-subtle h-2.5 flex-col border-t',
        className,
      )}
      {...props}
    >
      <ScrollAreaPrimitive.ScrollAreaThumb className="bg-border-strong hover:bg-fg-subtle relative flex-1 rounded-full transition-colors duration-100 ease-out" />
    </ScrollAreaPrimitive.ScrollAreaScrollbar>
  )
}

export { ScrollArea, ScrollBar }
